import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const SERVICES = [
    {
        n: "01",
        title: "Private Credit",
        tag: "Personal",
        copy: "Bespoke credit facilities for life's milestones — structured around your liquidity profile.",
        stats: [
            { k: "Ticket", v: "AED 50K – 5M" },
            { k: "Tenure", v: "12 – 84 mo" },
            { k: "From", v: "4.79% APR" },
        ],
        img: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=800&q=80",
        accent: "gold",
    },
    {
        n: "02",
        title: "Enterprise Capital",
        tag: "Business",
        copy: "Working capital, term facilities and trade instruments for ambitious SMEs scaling across the GCC.",
        stats: [
            { k: "Ticket", v: "Up to 60M" },
            { k: "Basis", v: "Cash-flow" },
            { k: "Decision", v: "48 hours" },
        ],
        img: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=800&q=80",
        accent: "forest",
    },
    {
        n: "03",
        title: "Residence Mortgage",
        tag: "Mortgage",
        copy: "Acquisition, construction and refinancing across Dubai, Abu Dhabi and Ras Al Khaimah.",
        stats: [
            { k: "LTV", v: "Up to 85%" },
            { k: "Tenure", v: "30 years" },
            { k: "Pre-approval", v: "5 days" },
        ],
        img: "https://images.unsplash.com/photo-1582407947304-fd86f028f716?auto=format&fit=crop&w=800&q=80",
        accent: "coral",
    },
    {
        n: "04",
        title: "Venture Conviction",
        tag: "Equity",
        copy: "Operator-led equity capital for founders shaping fintech, climate and deep-tech across MENA.",
        stats: [
            { k: "Stage", v: "Seed → B" },
            { k: "Cheque", v: "AED 2M – 120M" },
            { k: "Style", v: "Hands-on" },
        ],
        img: "https://images.unsplash.com/photo-1559526324-4b87b5e36e44?auto=format&fit=crop&w=800&q=80",
        accent: "sky",
    },
];

const ACCENT_CLASS = {
    gold: { bar: "bg-gold", text: "text-gold", badge: "bg-gold/10 text-gold border-gold/30" },
    forest: { bar: "bg-forest", text: "text-forest", badge: "bg-forest/10 text-forest border-forest/30" },
    coral: { bar: "bg-coral", text: "text-coral", badge: "bg-coral/10 text-coral border-coral/30" },
    sky: { bar: "bg-sky", text: "text-sky", badge: "bg-sky/10 text-sky border-sky/30" },
};

export default function Services() {
    return (
        <section
            id="services"
            data-testid="services-section"
            className="relative py-20 md:py-28 bg-white"
        >
            <div className="max-w-[1280px] mx-auto px-6 md:px-10">
                <div className="grid md:grid-cols-12 gap-8 mb-14">
                    <div className="md:col-span-6">
                        <div className="flex items-center gap-3 mb-5">
                            <span className="w-8 h-px bg-gold" />
                            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">
                                Four Mandates · One Compass
                            </span>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl leading-[1] tracking-tight font-medium text-ink">
                            Capital, <em className="italic text-gold font-normal">choreographed.</em>
                        </h2>
                    </div>
                    <div className="md:col-span-5 md:col-start-8 flex items-end">
                        <p className="text-ink-muted font-light text-lg leading-relaxed">
                            Whether you are protecting generational wealth, scaling a business, or backing the next founder — every QuickFin mandate is privately negotiated and engineered around your moment.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {SERVICES.map((s, i) => {
                        const ac = ACCENT_CLASS[s.accent];
                        return (
                            <motion.a
                                key={s.n}
                                href="#contact"
                                data-testid={`service-card-${s.n}`}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.6, delay: i * 0.08 }}
                                className="group relative warm-card overflow-hidden flex flex-col"
                            >
                                <div className="relative h-44 overflow-hidden">
                                    <img
                                        src={s.img}
                                        alt={s.title}
                                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-white/30 via-transparent to-transparent" />
                                    <div className={`absolute top-3 left-3 font-mono text-[10px] tracking-[0.3em] ${ac.text} bg-white/95 px-2 py-1`}>
                                        {s.n} / 04
                                    </div>
                                    <div className={`absolute top-3 right-3 font-mono text-[9px] uppercase tracking-[0.25em] border px-2 py-1 ${ac.badge}`}>
                                        {s.tag}
                                    </div>
                                </div>

                                <div className="p-5 flex flex-col justify-between flex-1">
                                    <div>
                                        <div className={`w-8 h-0.5 ${ac.bar} mb-3`} />
                                        <h3 className="font-serif text-2xl tracking-tight mb-2 text-ink">
                                            {s.title}
                                        </h3>
                                        <p className="text-ink-muted font-light leading-relaxed text-base">
                                            {s.copy}
                                        </p>
                                    </div>

                                    <div className="mt-5 pt-4 border-t border-cream-deep grid grid-cols-3 gap-2">
                                        {s.stats.map((st) => (
                                            <div key={st.k}>
                                                <div className="font-mono text-[8px] uppercase tracking-[0.15em] text-ink-muted">
                                                    {st.k}
                                                </div>
                                                <div className="font-serif text-sm text-ink mt-0.5">
                                                    {st.v}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className={`mt-4 inline-flex items-center gap-1.5 ${ac.text} font-mono text-[10px] uppercase tracking-[0.25em] group-hover:gap-2 transition-all`}>
                                        Discuss <ArrowUpRight size={12} />
                                    </div>
                                </div>
                            </motion.a>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
